import { useState, type ReactNode } from 'react';
import { Banner, Spinner, cx } from '../../design-system';
import type { SessionEndReason } from '../../sessionEnd';
import styles from './LoginScreen.module.css';
import logoUrl from '../../assets/knLogo.png';
import { PRODUCT_NAME } from '../../brand';

/**
 * Acceso Google — the entry point.
 *
 * A pure view: it receives the domain, the busy flag, an error message and one
 * callback. Everything about how sign-in actually works — the provider, the
 * blocking function, the claims — lives behind that callback, which is what makes
 * this component renderable in a test or a preview with three plain props.
 *
 * The copy is deliberately precise about what a successful sign-in does and does
 * not grant: a new corporate account is created in a pending state and sees nothing
 * until an administrator validates it, so promising access here would be a lie the
 * next screen would have to walk back.
 */

export interface LoginScreenProps {
  readonly companyName: string;
  /** Shown to the user, and passed to Google as the `hd` hint. */
  readonly allowedDomain: string;
  readonly signingIn: boolean;
  readonly error: string | null;
  readonly onSignIn: () => void;
  /** The demo reproduces the account chooser shown in the handoff. */
  readonly demoMode?: boolean;
  /**
   * Offers the QA password form under the Google button. Off unless the build
   * turns it on, and the copy says out loud what the accounts are for — a door
   * nobody can explain is one nobody dares close.
   */
  readonly passwordLogin?: boolean;
  /**
   * Por qué terminó la sesión anterior, si la cerró el portal y no la persona.
   *
   * `null` es lo normal —primera visita o «Salir»— y no pinta nada. Con valor,
   * la pantalla lo explica antes del botón: sin eso, quien dejó el portal abierto
   * vuelve y encuentra el acceso sin saber si algo falló.
   */
  readonly sessionEnded?: SessionEndReason | null;
  /** Para decir cuántos minutos, en el aviso de inactividad. */
  readonly idleMinutes?: number;
  readonly onSignInWithPassword?: (email: string, password: string) => void;
}

const DEMO_ACCOUNTS = [
  { name: 'Marco Rivas', email: 'marco.rivas@knotion.com', initials: 'MR' },
  { name: 'Ana Cifuentes', email: 'ana.cifuentes@knotion.com', initials: 'AC' },
] as const;

const PERKS: readonly { n: string; text: string }[] = [
  { n: '1', text: 'Elige fechas viendo quién más está fuera' },
  { n: '2', text: 'Sigue el estado sin escribir un correo' },
  { n: '3', text: 'Tu saldo y tus días por vencer, siempre a la vista' },
];

export const LoginScreen = ({
  companyName,
  allowedDomain,
  signingIn,
  error,
  onSignIn,
  demoMode = false,
  passwordLogin = false,
  onSignInWithPassword,
  sessionEnded = null,
  idleMinutes,
}: LoginScreenProps): ReactNode => {
  const [choosingAccount, setChoosingAccount] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  return (
    <main className={styles.page}>
    <div className={styles.card}>
      <div className={styles.promo}>
        <div className={styles.brand}>
          <img src={logoUrl} alt={companyName} className={styles.logo} />
          <span className={styles.brandDivider}>{PRODUCT_NAME}</span>
        </div>

        <div className={styles.promoBody}>
          <h1 className={styles.headline}>
            Pide tus días
            <br />
            sin escribir
            <br />
            un solo correo.
          </h1>
          <p className={styles.subhead}>
            Solicita, sigue el estado en tiempo real y mira quién está fuera en tu área antes de
            elegir fechas.
          </p>
        </div>

        <ul className={styles.perks}>
          {PERKS.map((perk) => (
            <li key={perk.n} className={styles.perk}>
              <span className={styles.perkNumber} aria-hidden="true">
                {perk.n}
              </span>
              {perk.text}
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.panel}>
        <div>
          <span className={styles.eyebrow}>Acceso corporativo</span>
          <h2 className={styles.title}>Entra con tu cuenta de Google</h2>
          <p className={styles.description}>
            Solo se permiten cuentas del dominio{' '}
            <strong className={styles.domain}>@{allowedDomain}</strong>. No hay contraseñas que
            recordar.
          </p>
        </div>

        {/*
          Dicho antes del botón y en tono de aviso, no de error: nada falló. El
          portal cerró la sesión a propósito, y quien vuelve tiene que saberlo
          para no pensar que perdió algo o que su cuenta cambió.
        */}
        {sessionEnded === 'idle' && (
          <Banner tone="info">
            <strong>Cerramos tu sesión para cuidar tu información.</strong>{' '}
            {idleMinutes === undefined
              ? 'Pasó un buen rato sin actividad.'
              : `Pasaron ${String(idleMinutes)} minutos sin actividad.`}{' '}
            Vuelve a entrar y sigue donde ibas.
          </Banner>
        )}
        {sessionEnded === 'expired' && (
          <Banner tone="info">
            <strong>Tu sesión expiró.</strong> Por seguridad, las sesiones caducan después de un
            rato. Entra de nuevo para continuar.
          </Banner>
        )}

        {demoMode && choosingAccount && !signingIn ? (
          <div className={styles.accountChooser} role="dialog" aria-label="Elige una cuenta">
            <div className={styles.accountChooserTitle}>Elige una cuenta</div>
            {DEMO_ACCOUNTS.map((account) => (
              <button
                key={account.email}
                type="button"
                className={styles.accountOption}
                onClick={() => {
                  setChoosingAccount(false);
                  onSignIn();
                }}
              >
                <span className={styles.accountAvatar} aria-hidden="true">
                  {account.initials}
                </span>
                <span className={styles.accountText}>
                  <strong>{account.name}</strong>
                  <span>{account.email}</span>
                </span>
              </button>
            ))}
            <button
              type="button"
              className={styles.accountCancel}
              onClick={() => {
                setChoosingAccount(false);
              }}
            >
              Volver
            </button>
          </div>
        ) : signingIn ? (
          <div className={styles.loading}>
            <Spinner label="Verificando" />
            <span className={styles.loadingText}>Verificando dominio corporativo…</span>
          </div>
        ) : (
          <button
            type="button"
            className={styles.googleButton}
            onClick={() => {
              if (demoMode) {
                setChoosingAccount(true);
              } else {
                onSignIn();
              }
            }}
            disabled={signingIn}
          >
            <span className={styles.googleMark} aria-hidden="true">
              <span className={styles.googleMarkInner} />
            </span>
            Continuar con Google
            <span className={styles.arrow} aria-hidden="true">
              →
            </span>
          </button>
        )}

        {/*
          * The QA door, and it stays shut until asked for.
          *
          * Collapsed behind a link rather than shown open: «no hay contraseñas
          * que recordar» is the promise two paragraphs above, and a password box
          * sitting under it reads as a contradiction to the thirty-seven people
          * for whom that promise is still true.
          */}
        {passwordLogin && !signingIn && onSignInWithPassword !== undefined && (
          <div className={styles.passwordBlock}>
            {showPasswordForm ? (
              <form
                className={styles.passwordForm}
                onSubmit={(event) => {
                  event.preventDefault();
                  onSignInWithPassword(email, password);
                }}
              >
                <label className={styles.passwordLabel} htmlFor="qa-email">
                  Correo
                </label>
                <input
                  id="qa-email"
                  className={styles.passwordInput}
                  type="email"
                  autoComplete="username"
                  placeholder={`cuenta@${allowedDomain}`}
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                  required
                />

                <label className={styles.passwordLabel} htmlFor="qa-password">
                  Contraseña
                </label>
                <input
                  id="qa-password"
                  className={styles.passwordInput}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                  }}
                  required
                />

                <button type="submit" className={styles.passwordSubmit} disabled={signingIn}>
                  Entrar
                </button>
                <p className={styles.passwordNote}>
                  Cuentas de prueba para QA. El acceso normal es con Google.
                </p>
              </form>
            ) : (
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => {
                  setShowPasswordForm(true);
                }}
              >
                Entrar con usuario y contraseña (pruebas)
              </button>
            )}
          </div>
        )}

        {error !== null && (
          <div className={styles.error} role="alert">
            <span className={styles.errorGlyph} aria-hidden="true">
              !
            </span>
            {error}
          </div>
        )}

        <p className={styles.legal}>
          Al ingresar aceptas la política de vacaciones vigente. Las cuentas nuevas quedan
          pendientes de validación del administrador del área.
        </p>
      </div>
    </div>
    </main>
  );
};

/**
 * What a validated-but-not-yet-activated account sees.
 *
 * This is the normal first experience of every new hire, not an error, and the copy
 * says so. The refresh button exists because claims are cached in the ID token: once
 * an administrator validates the account the person would otherwise wait up to an
 * hour for the token to rotate, and "vuelve a intentar" is a much better answer than
 * "cierra sesión y vuelve a entrar".
 */
export interface PendingValidationScreenProps {
  readonly email: string;
  readonly displayName: string;
  readonly companyName: string;
  readonly hrEmail: string;
  readonly checking: boolean;
  readonly onRefresh: () => void;
  readonly onSignOut: () => void;
}

export const PendingValidationScreen = ({
  email,
  displayName,
  companyName,
  hrEmail,
  checking,
  onRefresh,
  onSignOut,
}: PendingValidationScreenProps): ReactNode => (
  <main className={styles.page}>
    <div className={styles.card}>
      <div className={styles.promo}>
        <div className={styles.brand}>
          <img src={logoUrl} alt={companyName} className={styles.logo} />
          <span className={styles.brandDivider}>{PRODUCT_NAME}</span>
        </div>
        <div className={styles.promoBody}>
          <h1 className={styles.headline}>
            Tu cuenta
            <br />
            está en camino.
          </h1>
          <p className={styles.subhead}>
            Verificamos tu correo corporativo. Falta que el administrador de tu área te asigne una
            sub área para que puedas empezar.
          </p>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.pending}>
          <span className={styles.eyebrow}>Pendiente de validación</span>
          <h2 className={styles.title}>Hola, {displayName}</h2>
          <p className={styles.description}>
            Entraste con <strong className={styles.domain}>{email}</strong>. En cuanto RR.HH. valide
            tu cuenta verás tu saldo y podrás solicitar días.
          </p>
          <p className={styles.legal}>
            Si llevas más de un día así, escribe a{' '}
            <a href={`mailto:${hrEmail}`}>{hrEmail}</a>.
          </p>

          <div className={styles.pendingActions}>
            <button
              type="button"
              className={cx(styles.googleButton, styles.refreshButton)}
              onClick={onRefresh}
              disabled={checking}
            >
              {checking ? <Spinner label="Revisando" /> : <span aria-hidden="true">↺</span>}
              {checking ? 'Revisando…' : 'Ya me validaron, revisar'}
            </button>
          </div>

          <button type="button" className={styles.signOutLink} onClick={onSignOut}>
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  </main>
);

/**
 * Las claims son válidas pero el portal no pudo leer la ficha: la red.
 *
 * **No afirma nada de la cuenta**, y ésa es toda su razón de existir. Este caso
 * caía en «Pendiente de validación», y un superusuario activo leía que su cuenta
 * «está en camino» y que RR.HH. tenía que validarla — al despertar el portátil,
 * con la red todavía reconectando.
 */
export interface SessionUnreachableScreenProps {
  readonly companyName: string;
  readonly checking: boolean;
  readonly onRetry: () => void;
  readonly onSignOut: () => void;
}

export const SessionUnreachableScreen = ({
  companyName,
  checking,
  onRetry,
  onSignOut,
}: SessionUnreachableScreenProps): ReactNode => (
  <main className={styles.page}>
    <div className={styles.card}>
      <div className={styles.promo}>
        <div className={styles.brand}>
          <img src={logoUrl} alt={companyName} className={styles.logo} />
          <span className={styles.brandDivider}>{PRODUCT_NAME}</span>
        </div>
        <div className={styles.promoBody}>
          <h1 className={styles.headline}>
            No pudimos
            <br />
            cargar el portal.
          </h1>
          <p className={styles.subhead}>
            No es tu cuenta: lo que falló fue la conexión con el portal.
          </p>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.pending}>
          <span className={styles.eyebrow}>Sin conexión</span>
          <h2 className={styles.title}>No logramos conectar</h2>
          <p className={styles.description}>
            Suele pasar al despertar el equipo o al cambiar de red. Revisa tu conexión y vuelve a
            intentarlo.
          </p>

          <div className={styles.pendingActions}>
            <button
              type="button"
              className={cx(styles.googleButton, styles.refreshButton)}
              onClick={onRetry}
              disabled={checking}
            >
              {checking ? <Spinner label="Reintentando" /> : <span aria-hidden="true">↺</span>}
              {checking ? 'Reintentando…' : 'Reintentar'}
            </button>
          </div>

          <button type="button" className={styles.signOutLink} onClick={onSignOut}>
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  </main>
);

/** A deactivated account. Terminal from the app's side; only HR can undo it. */
export const InactiveAccountScreen = ({
  companyName,
  hrEmail,
  onSignOut,
}: {
  companyName: string;
  hrEmail: string;
  onSignOut: () => void;
}): ReactNode => (
  <main className={styles.page}>
    <div className={styles.card}>
      <div className={styles.promo}>
        <div className={styles.brand}>
          <img src={logoUrl} alt={companyName} className={styles.logo} />
          <span className={styles.brandDivider}>{PRODUCT_NAME}</span>
        </div>
      </div>
      <div className={styles.panel}>
        <h2 className={styles.title}>Tu cuenta está desactivada</h2>
        <p className={styles.description}>
          Un administrador desactivó este acceso. Si crees que es un error, escribe a{' '}
          <a href={`mailto:${hrEmail}`}>{hrEmail}</a>.
        </p>
        <button type="button" className={styles.googleButton} onClick={onSignOut}>
          Cerrar sesión
        </button>
      </div>
    </div>
  </main>
);
